<script setup>
import { toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWhatsappTemplates } from 'dashboard/composables/useWhatsappTemplates';

// Rodízio de atendentes das importações de lead (Meta Lead Ads e Google Lead
// Ads). Cada pessoa do rodízio pode ter a PRÓPRIA caixa de WhatsApp, porque na
// prática cada vendedor fala do próprio número — e, quando esse número é
// oficial, a Meta só entrega template aprovado para quem nunca respondeu.
// Por isso a linha tem três partes: quem atende, por qual caixa, com qual
// template.
const props = defineProps({
  agents: { type: Array, default: () => [] },
  inboxes: { type: Array, default: () => [] },
});

const userIds = defineModel('userIds', { type: Array, default: () => [] });
const routing = defineModel('routing', { type: Object, default: () => ({}) });
const onlineOnly = defineModel('onlineOnly', { type: Boolean, default: false });

const { t } = useI18n();

// Mesma busca de templates da caixa geral da integração: só caixa oficial
// tem, e só quando alguém escolhe aquela caixa.
const {
  ehOficial,
  carregar: carregarTemplates,
  templatesDe,
  estaCarregando,
} = useWhatsappTemplates(toRef(props, 'inboxes'));

const noRodizio = id => userIds.value.includes(id);

const rotaDe = id => routing.value[String(id)] || {};

const alterarRota = (id, patch) => {
  routing.value = {
    ...routing.value,
    [String(id)]: { ...rotaDe(id), ...patch },
  };
};

const alternarNoRodizio = id => {
  if (noRodizio(id)) {
    userIds.value = userIds.value.filter(item => item !== id);
    const resto = { ...routing.value };
    delete resto[String(id)];
    routing.value = resto;
    return;
  }
  userIds.value = [...userIds.value, id];
};

const escolherCaixa = (id, inboxId) => {
  const numero = Number(inboxId) || null;
  // Trocar de caixa invalida o template: ele pertence à caixa anterior.
  alterarRota(id, { inbox_id: numero, template_name: null });
  carregarTemplates(numero);
};

// Ao abrir a tela com rotas já salvas, buscar os templates das caixas oficiais
// que já estão escolhidas, senão o seletor aparece vazio com valor definido.
watch(
  () => [userIds.value, props.inboxes.length],
  () => {
    userIds.value.forEach(id => carregarTemplates(rotaDe(id).inbox_id));
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-0.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('LEAD_ASSIGNMENT.TITLE') }}
      </span>
      <span class="text-xs text-n-slate-11">
        {{ t('LEAD_ASSIGNMENT.HINT') }}
      </span>
    </div>

    <span v-if="!agents.length" class="text-xs text-n-slate-11">
      {{ t('LEAD_ASSIGNMENT.NO_AGENTS') }}
    </span>

    <div v-else class="flex flex-col gap-2" data-testid="assignment-pool">
      <div
        v-for="agent in agents"
        :key="agent.id"
        class="flex flex-col gap-2 rounded-lg border p-2.5 md:flex-row md:items-center"
        :class="
          noRodizio(agent.id)
            ? 'border-n-brand bg-n-brand/5'
            : 'border-n-weak bg-n-solid-2'
        "
      >
        <button
          type="button"
          class="flex flex-1 items-center gap-1.5 text-left text-xs font-medium"
          :class="noRodizio(agent.id) ? 'text-n-slate-12' : 'text-n-slate-11'"
          @click="alternarNoRodizio(agent.id)"
        >
          <span
            :class="
              noRodizio(agent.id)
                ? 'i-lucide-check-circle-2'
                : 'i-lucide-circle'
            "
            class="size-3.5"
          />
          {{ agent.name }}
        </button>

        <template v-if="noRodizio(agent.id)">
          <select
            :value="rotaDe(agent.id).inbox_id || ''"
            class="form-select md:w-56"
            @change="escolherCaixa(agent.id, $event.target.value)"
          >
            <option value="">{{ t('LEAD_ASSIGNMENT.INBOX_DEFAULT') }}</option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>

          <select
            v-if="ehOficial(rotaDe(agent.id).inbox_id)"
            :value="rotaDe(agent.id).template_name || ''"
            class="form-select md:w-56"
            @change="
              alterarRota(agent.id, { template_name: $event.target.value })
            "
          >
            <option value="">
              {{
                estaCarregando(rotaDe(agent.id).inbox_id)
                  ? t('LEAD_ASSIGNMENT.TEMPLATE_LOADING')
                  : t('LEAD_ASSIGNMENT.TEMPLATE_EMPTY')
              }}
            </option>
            <option
              v-for="modelo in templatesDe(rotaDe(agent.id).inbox_id)"
              :key="modelo.id || modelo.name"
              :value="modelo.name"
            >
              {{ modelo.name }}
            </option>
          </select>
        </template>
      </div>
    </div>

    <p
      v-if="userIds.length && agents.length"
      class="text-xs text-n-slate-11 bg-n-alpha-1 rounded-lg px-3 py-2"
    >
      {{ t('LEAD_ASSIGNMENT.OFFICIAL_HINT') }}
    </p>

    <label v-if="userIds.length" class="flex items-center gap-2">
      <input v-model="onlineOnly" type="checkbox" />
      <span class="text-sm text-n-slate-12">
        {{ t('LEAD_ASSIGNMENT.ONLINE_ONLY') }}
      </span>
    </label>
    <span v-if="userIds.length" class="text-xs text-n-slate-11">
      {{ t('LEAD_ASSIGNMENT.ONLINE_HINT') }}
    </span>
  </div>
</template>
